import cn from 'classnames'
import { GUID } from '@hoxtonmix/utils'
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
  CheckCircleIcon,
  CircleXIcon,
  InfoIcon,
  WarningIcon,
} from 'components/icons'

export type Theme = 'success' | 'danger' | 'warning' | 'info'

type IconProps = { fill?: string; className?: string }

const THEME_CONFIG: Record<Theme, { icon: FC<IconProps>; color: string }> = {
  danger: { color: '#EF4D56', icon: CircleXIcon },
  info: { color: '#449FC4', icon: InfoIcon },
  success: { color: '#4CB67E', icon: CheckCircleIcon },
  warning: { color: '#E49A43', icon: WarningIcon },
}

const FADE_DELAY = 10
const FADE_DURATION = 200

// --- ToastMessage ---

type ToastMessageProps = {
  children?: ReactNode
  expiry?: number
  fade?: boolean
  hideClose?: boolean
  isRemoving?: boolean
  remove: () => void
  theme?: Theme
  title?: ReactNode
}

export const ToastMessage: FC<ToastMessageProps> = ({
  children,
  expiry,
  fade,
  hideClose,
  isRemoving,
  remove,
  theme = 'success',
  title,
}) => {
  const [isShowing, setIsShowing] = useState(true)
  const removeRef = useRef(remove)
  removeRef.current = remove

  const handleRemove = useCallback(() => {
    setIsShowing(true)
    setTimeout(() => removeRef.current(), fade ? FADE_DURATION : 0)
  }, [fade])

  useEffect(() => {
    const showTimer = setTimeout(() => setIsShowing(false), FADE_DELAY)
    const expiryTimer = expiry
      ? setTimeout(() => {
          setIsShowing(true)
          handleRemove()
        }, expiry)
      : undefined

    return () => {
      clearTimeout(showTimer)
      if (expiryTimer) clearTimeout(expiryTimer)
    }
  }, [expiry, handleRemove])

  const { color, icon: Icon } = THEME_CONFIG[theme]

  return (
    <div
      className={cn('toast mb-4 outline bg-white p-2 shadow rounded', {
        'fade': fade,
        'hide': isRemoving,
        'show': true,
        'showing': fade && isShowing,
      })}
    >
      <div className={`toast-border bg-${theme}`} />
      <div className='toast-header rounded flex-row align-items-center'>
        <Icon className='me-2' fill={color} />
        <div className='flex-fill'>
          <h5 className='mb-0 fw-semibold'>{title}</h5>
          <div className='fw-normal'>{children}</div>
        </div>
        {!hideClose && (
          <i
            onClick={handleRemove}
            className='cursor-pointer ms-4 fs-sm text-light600 close fa fa-times'
          />
        )}
      </div>
    </div>
  )
}

// --- ToastContainer ---

type ToastMessageData = {
  children: ReactNode
  expiry: number
  id: string
  isRemoving?: boolean
  theme?: Theme
  title: ReactNode
}

export let toast: (
  title: ReactNode,
  children?: ReactNode,
  theme?: Theme,
  expiry?: number,
) => void

export const ToastContainer: FC = () => {
  const [messages, setMessages] = useState<ToastMessageData[]>([])

  const addToast = useCallback(
    (
      title: ReactNode,
      children: ReactNode,
      theme: Theme = 'success',
      expiry = 5000,
    ) => {
      setMessages((prev) => [
        ...prev,
        { children, expiry, id: GUID(), theme, title },
      ])
    },
    [],
  )

  const removeToast = useCallback((id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id))
  }, [])

  useEffect(() => {
    toast = addToast
  }, [addToast])

  return (
    <div className='toast-messages toast-container position-fixed top-0 end-0 m-4'>
      {messages.map((message) => (
        <ToastMessage
          fade
          key={message.id}
          title={message.title}
          theme={message.theme}
          isRemoving={message.isRemoving}
          remove={() => removeToast(message.id)}
          expiry={message.expiry}
        >
          {message.children}
        </ToastMessage>
      ))}
    </div>
  )
}
